import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n";
import {
  COOKIE_STORAGE_MAX_AGE,
  clearDeviceRecognitionStorage,
  readCookiePreferences
} from "../Tools/fingerprintConsent";
import "./CookieConsent.css";

const english = {
  title: "Your privacy choices",
  intro: "BublyUp uses essential storage for sign-in and your language. Optional device recognition helps protect accounts using a device ID and browser signals. It is off unless you allow it.",
  learn: "Read the cookie and device-storage notice",
  reject: "Reject optional",
  allow: "Allow device recognition",
  settings: "Choose settings",
  settingsTitle: "Cookie and device settings",
  necessary: "Essential and preference storage",
  necessaryText: "Always on: sign-in, security, language, and remembering this choice.",
  security: "Optional device recognition",
  securityText: "A first-party device ID and browser/device signals may be used during sign-in to help detect account misuse. This does not provide precise location.",
  save: "Save choices",
  close: "Close"
};
const french = {
  title: "Vos choix de confidentialité",
  intro: "BublyUp utilise le stockage nécessaire à la connexion et à votre langue. La reconnaissance facultative de l’appareil aide à protéger les comptes à l’aide d’un identifiant et de signaux du navigateur. Elle reste désactivée sans votre accord.",
  learn: "Lire l’avis sur les cookies et le stockage",
  reject: "Refuser l’optionnel",
  allow: "Autoriser la reconnaissance",
  settings: "Choisir les réglages",
  settingsTitle: "Réglages des cookies et de l’appareil",
  necessary: "Stockage essentiel et préférences",
  necessaryText: "Toujours actif : connexion, sécurité, langue et mémorisation de ce choix.",
  security: "Reconnaissance facultative de l’appareil",
  securityText: "Un identifiant de première partie et des signaux du navigateur/appareil peuvent aider à repérer un usage abusif lors de la connexion. Cela ne donne pas une localisation précise.",
  save: "Enregistrer mes choix",
  close: "Fermer"
};

export default function CookieConsent() {
  const { language } = useLanguage();
  const copy = language === "fr" ? french : english;
  const [preferences, setPreferences] = useState(undefined);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [securityFingerprint, setSecurityFingerprint] = useState(false);

  useEffect(() => {
    const saved = readCookiePreferences();
    if (!saved) {
      clearDeviceRecognitionStorage();
      setPreferences(null);
    } else {
      setPreferences(saved);
      setSecurityFingerprint(saved.securityFingerprint === true);
    }

    const openSettings = () => setSettingsOpen(true);
    window.addEventListener("bublyup:open-cookie-settings", openSettings);
    return () => window.removeEventListener("bublyup:open-cookie-settings", openSettings);
  }, []);

  const save = (allowDeviceRecognition) => {
    const next = {
      version: 1,
      decidedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + COOKIE_STORAGE_MAX_AGE).toISOString(),
      securityFingerprint: allowDeviceRecognition
    };
    try {
      localStorage.setItem("bublyup_cookie_preferences", JSON.stringify(next));
    } catch (_error) {
      // The current choice still applies for this page session.
    }
    if (!allowDeviceRecognition) clearDeviceRecognitionStorage();
    setSecurityFingerprint(allowDeviceRecognition);
    setPreferences(next);
    setSettingsOpen(false);
  };

  if (preferences === undefined) return null;

  return (
    <>
      {preferences === null && !settingsOpen && (
        <aside className="bu-cookie-banner" role="dialog" aria-labelledby="bu-cookie-title" aria-describedby="bu-cookie-intro">
          <div className="bu-cookie-copy">
            <h2 id="bu-cookie-title">{copy.title}</h2>
            <p id="bu-cookie-intro">{copy.intro}</p>
            <Link to="/cookies">{copy.learn}</Link>
          </div>
          <div className="bu-cookie-actions">
            <button type="button" className="bu-cookie-secondary" onClick={() => save(false)}>{copy.reject}</button>
            <button type="button" className="bu-cookie-secondary" onClick={() => setSettingsOpen(true)}>{copy.settings}</button>
            <button type="button" className="bu-cookie-primary" onClick={() => save(true)}>{copy.allow}</button>
          </div>
        </aside>
      )}

      {settingsOpen && (
        <div className="bu-cookie-backdrop" role="presentation">
          <section className="bu-cookie-settings" role="dialog" aria-modal="true" aria-labelledby="bu-cookie-settings-title">
            <button type="button" className="bu-cookie-close" aria-label={copy.close} onClick={() => setSettingsOpen(false)}>×</button>
            <h2 id="bu-cookie-settings-title">{copy.settingsTitle}</h2>
            <div className="bu-cookie-setting-row">
              <div><strong>{copy.necessary}</strong><p>{copy.necessaryText}</p></div>
              <span className="bu-cookie-always">Always on</span>
            </div>
            <label className="bu-cookie-setting-row bu-cookie-toggle-row">
              <div><strong>{copy.security}</strong><p>{copy.securityText}</p></div>
              <input type="checkbox" checked={securityFingerprint} onChange={(event) => setSecurityFingerprint(event.target.checked)} />
            </label>
            <div className="bu-cookie-actions">
              <button type="button" className="bu-cookie-secondary" onClick={() => save(false)}>{copy.reject}</button>
              <button type="button" className="bu-cookie-primary" onClick={() => save(securityFingerprint)}>{copy.save}</button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
